/**
 * ImportResultsModal Component
 * Displays import results with summary stats and detailed track listings
 *
 * Features:
 * - Summary stats with colored badges (found/ambiguous/not_found)
 * - Automatic status detection (success/partial/failed)
 * - Track lists grouped by status with collapsible sections
 * - Copy failed tracks to clipboard
 * - Helpful tips when tracks fail
 * - Keyboard support (Esc to close)
 * - Full accessibility (ARIA labels)
 */

import { useState } from "react";
import { Modal, ModalActions } from "../shared/Modal";
import { Button } from "../shared/Button";
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Info,
} from "lucide-react";
import type {
  PlaylistImportResponse,
  ImportMatchSummary,
} from "../../api/types";

export interface ImportResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: PlaylistImportResponse;
  mode: "add" | "replace";
}

type ImportState = "success" | "partial" | "failed";

interface TracksByStatus {
  not_found: ImportMatchSummary[];
  ambiguous: ImportMatchSummary[];
  found: ImportMatchSummary[];
}

/**
 * Determine overall import status based on results
 */
function getImportState(results: PlaylistImportResponse): ImportState {
  if (results.not_found === results.total_lines) return "failed";
  if (results.found === results.total_lines) return "success";
  return "partial";
}

/**
 * Get status configuration (icon, color, title)
 */
function getStatusConfig(state: ImportState) {
  switch (state) {
    case "success":
      return {
        icon: CheckCircle2,
        color: "emerald",
        title: "Import Successful",
        description: "All tracks were found and imported.",
      };
    case "partial":
      return {
        icon: AlertCircle,
        color: "amber",
        title: "Partial Import",
        description: "Some tracks could not be matched.",
      };
    case "failed":
      return {
        icon: XCircle,
        color: "red",
        title: "Import Failed",
        description: "No tracks were found.",
      };
  }
}

export function ImportResultsModal({
  isOpen,
  onClose,
  results,
  mode,
}: ImportResultsModalProps) {
  // State for collapsible sections
  const [expandNotFound, setExpandNotFound] = useState(true);
  const [expandAmbiguous, setExpandAmbiguous] = useState(true);
  const [expandFound, setExpandFound] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Determine import state
  const importState = getImportState(results);
  const statusConfig = getStatusConfig(importState);
  const StatusIcon = statusConfig.icon;

  // Group tracks by status
  const tracksByStatus: TracksByStatus = {
    not_found: results.matches.filter((m) => m.status === "not_found"),
    ambiguous: results.matches.filter((m) => m.status === "ambiguous"),
    found: results.matches.filter((m) => m.status === "found"),
  };

  /**
   * Copy failed tracks to clipboard
   */
  const copyFailedTracks = async () => {
    const failedLines = tracksByStatus.not_found.map((m) => m.line).join("\n");

    try {
      await navigator.clipboard.writeText(failedLines);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Import Results"
      className="w-full max-w-3xl"
    >
      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Status Header */}
        <div className="flex items-start gap-4">
          <div
            className={`
              flex-shrink-0 p-3 rounded-lg
              ${statusConfig.color === "emerald" && "bg-emerald-900/30 text-emerald-400"}
              ${statusConfig.color === "amber" && "bg-amber-900/30 text-amber-400"}
              ${statusConfig.color === "red" && "bg-red-900/30 text-red-400"}
            `}
          >
            <StatusIcon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">
              {statusConfig.title}
            </h3>
            <p className="text-sm text-zinc-400 mt-1">
              {statusConfig.description}
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          {/* Found */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Found</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">
                {results.found}
              </span>
              <span className="text-sm text-zinc-500">
                / {results.total_lines}
              </span>
            </div>
            {results.found > 0 && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/30 text-emerald-400">
                  {Math.round((results.found / results.total_lines) * 100)}%
                </span>
              </div>
            )}
          </div>

          {/* Ambiguous */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Ambiguous</span>
              <AlertCircle className="w-4 h-4 text-amber-400" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">
                {results.ambiguous}
              </span>
              <span className="text-sm text-zinc-500">
                / {results.total_lines}
              </span>
            </div>
            {results.ambiguous > 0 && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-900/30 text-amber-400">
                  {Math.round((results.ambiguous / results.total_lines) * 100)}%
                </span>
              </div>
            )}
          </div>

          {/* Not Found */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Not Found</span>
              <XCircle className="w-4 h-4 text-red-400" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">
                {results.not_found}
              </span>
              <span className="text-sm text-zinc-500">
                / {results.total_lines}
              </span>
            </div>
            {results.not_found > 0 && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/30 text-red-400">
                  {Math.round((results.not_found / results.total_lines) * 100)}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Info */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
          <p className="text-sm text-zinc-300">
            <span className="font-medium">
              {mode === "add"
                ? "Added to playlist:"
                : "Replaced playlist with:"}
            </span>{" "}
            <span className="text-emerald-400 font-semibold">
              {results.added_tracks}
            </span>{" "}
            track{results.added_tracks !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Helpful Tips (shown when there are failures) */}
        {(results.not_found > 0 || results.ambiguous > 0) && (
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-300 mb-2">
                  Tips for Better Results
                </h4>
                <ul className="space-y-1 text-sm text-blue-200/80">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>Check spelling of artist names and track titles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>Use format: "Artist - Track" or "Artist: Track"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>
                      Ambiguous matches found multiple results - try being more
                      specific
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Track Lists */}
        <div className="space-y-4">
          {/* Not Found Tracks */}
          {tracksByStatus.not_found.length > 0 && (
            <div className="border border-red-700/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandNotFound(!expandNotFound)}
                className="w-full flex items-center justify-between p-4 bg-red-900/20 hover:bg-red-900/30 transition-colors"
                aria-expanded={expandNotFound}
                aria-label="Toggle not found tracks list"
              >
                <div className="flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="font-medium text-red-300">
                    Not Found ({tracksByStatus.not_found.length})
                  </span>
                </div>
                {expandNotFound ? (
                  <ChevronUp className="w-5 h-5 text-red-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-red-400" />
                )}
              </button>
              {expandNotFound && (
                <div className="p-4 bg-zinc-900/50 space-y-2 max-h-64 overflow-y-auto">
                  {tracksByStatus.not_found.map((match, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-2 bg-zinc-800/50 border border-zinc-700 rounded text-sm"
                    >
                      <span className="text-zinc-500 font-mono text-xs mt-0.5">
                        {index + 1}.
                      </span>
                      <span className="text-zinc-300 flex-1">{match.line}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Ambiguous Tracks */}
          {tracksByStatus.ambiguous.length > 0 && (
            <div className="border border-amber-700/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandAmbiguous(!expandAmbiguous)}
                className="w-full flex items-center justify-between p-4 bg-amber-900/20 hover:bg-amber-900/30 transition-colors"
                aria-expanded={expandAmbiguous}
                aria-label="Toggle ambiguous tracks list"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  <span className="font-medium text-amber-300">
                    Ambiguous ({tracksByStatus.ambiguous.length})
                  </span>
                </div>
                {expandAmbiguous ? (
                  <ChevronUp className="w-5 h-5 text-amber-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-amber-400" />
                )}
              </button>
              {expandAmbiguous && (
                <div className="p-4 bg-zinc-900/50 space-y-2 max-h-64 overflow-y-auto">
                  {tracksByStatus.ambiguous.map((match, index) => (
                    <div
                      key={index}
                      className="p-2 bg-zinc-800/50 border border-zinc-700 rounded space-y-1"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-zinc-500 font-mono text-xs mt-0.5">
                          {index + 1}.
                        </span>
                        <div className="flex-1">
                          <div className="text-sm text-zinc-300">
                            {match.line}
                          </div>
                          {match.track_name && (
                            <div className="text-xs text-amber-400 mt-1">
                              Selected: {match.track_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Found Tracks */}
          {tracksByStatus.found.length > 0 && (
            <div className="border border-emerald-700/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandFound(!expandFound)}
                className="w-full flex items-center justify-between p-4 bg-emerald-900/20 hover:bg-emerald-900/30 transition-colors"
                aria-expanded={expandFound}
                aria-label="Toggle found tracks list"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="font-medium text-emerald-300">
                    Found ({tracksByStatus.found.length})
                  </span>
                </div>
                {expandFound ? (
                  <ChevronUp className="w-5 h-5 text-emerald-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-emerald-400" />
                )}
              </button>
              {expandFound && (
                <div className="p-4 bg-zinc-900/50 space-y-2 max-h-64 overflow-y-auto">
                  {tracksByStatus.found.map((match, index) => (
                    <div
                      key={index}
                      className="p-2 bg-zinc-800/50 border border-zinc-700 rounded space-y-1"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-zinc-500 font-mono text-xs mt-0.5">
                          {index + 1}.
                        </span>
                        <div className="flex-1">
                          <div className="text-sm text-zinc-300">
                            {match.line}
                          </div>
                          {match.track_name && (
                            <div className="text-xs text-emerald-400 mt-1">
                              Matched: {match.track_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <ModalActions>
        {tracksByStatus.not_found.length > 0 && (
          <Button
            variant="outline"
            onClick={copyFailedTracks}
            className="mr-auto"
            aria-label="Copy failed tracks to clipboard"
          >
            <Copy className="w-4 h-4" />
            {copyFeedback ? "Copied!" : "Copy Failed List"}
          </Button>
        )}
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </ModalActions>
    </Modal>
  );
}
